import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../demoMode';
import { ScreenShell } from './ScreenShared';

function PreviewRequired({ token, onPreviewUploaded, onPreviewSkipped }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [cameraReady, setCameraReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => {});
      }

      setCameraReady(true);
    } catch (err) {
      setError(err?.message || 'Unable to access camera/microphone. Please allow permissions and try again.');
    }
  };

  const uploadBlob = async (blob) => {
    setUploading(true);
    setError('');

    try {
      const form = new FormData();
      form.append('video', blob, 'preview.webm');

      const response = await fetch(`${API_URL}/v1/user/preview-video`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: form,
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || 'Unable to upload preview video.');
      }

      if (typeof onPreviewUploaded === 'function') {
        onPreviewUploaded(payload?.video_url || true);
      }

      navigate('/lounge', { replace: true });
    } catch (err) {
      setError(err.message || 'Unable to upload preview video.');
    } finally {
      setUploading(false);
    }
  };

  const startRecording = () => {
    if (!streamRef.current) {
      setError('Enable camera first.');
      return;
    }

    setError('');
    setSecondsLeft(5);
    chunksRef.current = [];

    const preferredMimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm'
    ];

    const mimeType = preferredMimeTypes.find((type) => {
      try {
        return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type);
      } catch {
        return false;
      }
    });

    try {
      const recorder = new MediaRecorder(streamRef.current, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        setRecording(false);
        setSecondsLeft(5);
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        uploadBlob(blob);
      };

      recorder.start();
      setRecording(true);

      const interval = window.setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            window.clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      window.setTimeout(() => {
        window.clearInterval(interval);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, 5000);
    } catch (err) {
      setRecording(false);
      setSecondsLeft(5);
      setError(err?.message || 'Unable to record preview video.');
    }
  };

  return (
    <ScreenShell>
      <h2 className="figma-section-title">Add your video preview</h2>
      <p className="figma-sub">Record a live 5-second preview before entering the lounge.</p>

      {error ? <div className="error-message">{error}</div> : null}

      <div className="figma-list-card" style={{ padding: '10px' }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{ width: '100%', borderRadius: '10px', background: '#000', minHeight: '240px' }}
        />
      </div>

      {!cameraReady ? (
        <button
          type="button"
          className="figma-btn figma-btn-primary"
          onClick={startCamera}
          disabled={uploading}
        >
          Enable camera
        </button>
      ) : (
        <button
          type="button"
          className="figma-btn figma-btn-primary"
          onClick={startRecording}
          disabled={recording || uploading}
        >
          {uploading
            ? 'Uploading...'
            : recording
              ? `Recording... ${secondsLeft}s`
              : 'Record 5-second preview'}
        </button>
      )}

      <button
        type="button"
        className="figma-btn figma-btn-secondary"
        onClick={() => {
          if (typeof onPreviewSkipped === 'function') {
            onPreviewSkipped();
          }
          navigate('/lounge', { replace: true });
        }}
        disabled={uploading || recording}
      >
        Skip preview
      </button>
    </ScreenShell>
  );
}

export default PreviewRequired;
